// src/pages/DealersStockView/components/DealerPartsTable.jsx
import React, { useMemo, useState, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLanguage } from '../../../contexts/LanguageContext';
import { createTruncatedTextForTable } from '../../../utils/textUtils';

const DealerPartsTable = ({ dealerName, addressName, rows, totalCount, pagination, onPageChange, loading, onOpenHistory }) => {
    const { th } = useLanguage();

    // Client-side sort (PrimeReact controlled)
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);

    const normalized = Array.isArray(rows) ? rows : [];

    const sortedData = useMemo(() => {
        if (!sortField || sortOrder === 0) return normalized;

        return [...normalized].sort((a, b) => {
            let aValue = a?.[sortField];
            let bValue = b?.[sortField];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortOrder;
            if (bValue == null) return -sortOrder;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }
            if (sortField.toLowerCase().includes('date')) {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortOrder * (aValue - bValue);
            }
            if (aValue < bValue) return -sortOrder;
            if (aValue > bValue) return sortOrder;
            return 0;
        });
    }, [normalized, sortField, sortOrder]);

    const handleSort = useCallback((e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    }, []);

    const actionBodyTemplate = (row) => (
        <div className="flex items-center gap-3">
            <i onClick={() => onOpenHistory?.(row)} className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('dealersStockView.common.history')} />
        </div>
    );

    return (
        <div className="mt-4">
            <DataTable
                value={sortedData}
                dataKey="id"
                lazy
                paginator
                first={(pagination?.PageNumber - 1) * (pagination?.PageSize || 10)}
                rows={pagination?.PageSize || 10}
                totalRecords={totalCount}
                onPage={onPageChange}
                onSort={handleSort}
                sortField={sortField}
                sortOrder={sortOrder}
                loading={loading}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                showGridlines
                stripedRows
                emptyMessage={th('dealersStockView.customer.noData')}
                tableStyle={{ minWidth: '60rem' }}
                sortMode="single"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={th('table.paginatorTemplate')}
            >
                <Column body={actionBodyTemplate} exportable={false} style={{ width: '3%', minWidth: '53px' }} />
                <Column field="partCode" header={th('dealersStockView.stock.partCode')} sortable style={{ width: '9%' }} />
                <Column field="partDesc" header={th('dealersStockView.stock.partDesc')} body={(row) => createTruncatedTextForTable(row.partDesc || row.PartDesc, 80)} sortable style={{ width: '16%' }} />
                <Column field="brandName" header={th('dealersStockView.stock.brandName')} sortable style={{ width: '9%' }} />
                <Column field="uom" header={th('dealersStockView.stock.uom')} style={{ width: '6%' }} />
                <Column field="quantity" header={th('dealersStockView.stock.quantity')} sortable style={{ width: '7%' }} />
                <Column field="warehouseName" header={th('dealersStockView.stock.warehouse')} style={{ width: '10%' }} />
                <Column field="block" header={th('dealersStockView.stock.block')} sortable style={{ width: '7%' }} />
                <Column field="shelf" header={th('dealersStockView.stock.shelf')} sortable style={{ width: '7%' }} />
                <Column field="sequence" header={th('dealersStockView.stock.sequence')} sortable style={{ width: '7%' }} />
                <Column field="eyes" header={th('dealersStockView.stock.eyes')} sortable style={{ width: '7%' }} />
            </DataTable>
        </div>
    );
};

export default DealerPartsTable;
